import { useEffect, useState } from 'react';
import { classDisplayName, type ClassDto } from '../../lib/reference';
import { hexaRollup, isErdaLinkClass } from '../../lib/hexaOverview';
import type { HexaNodeDto, HexaStatNodeDto } from '../../lib/characterProgress';
import { HexaNodeRingStrip, RingProgress } from '../goals/HexaSection';
import { IconHexa } from '../icons';
import { EstimatePrefix } from './EstimatePrefix';
import { HexaCurrencyIcon } from './HexaCurrencyIcon';

export interface HexaOverviewCharacter {
  characterId: number;
  name: string;
  /** Already-resolved `<img>` src (or `null` for the initial-letter fallback) — REV23
   *  (docs/specs/REV23-share-profile.md) needed this component to work for both the authenticated
   *  proxy route and the public share-token route, so the URL is built by the caller (`Hexa.tsx` via
   *  `avatarProxyPath`, `SharedProfile.tsx` via `sharedProfileAvatarPath`) rather than in here. */
  avatarSrc: string | null;
  classSlug: string;
  level: number;
  nodes: HexaNodeDto[];
  /** REV22 Fix 2 (docs/specs/REV22-hexa-stat-and-merged-masteries.md). */
  statNodes: HexaStatNodeDto[];
}

interface HexaOverviewProps {
  characters: HexaOverviewCharacter[];
  classes: ClassDto[];
}

/** Row avatar — same fallback-to-initial idiom as `CharacterTabStrip`'s `TabAvatar` / `PitchedOverview`'s `LuckiestAvatar`. */
function RowAvatar({ src, name }: { src: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-elevated-hover text-xs font-semibold text-text-faint">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" className="size-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{name.trim().slice(0, 1).toUpperCase() || '?'}</span>
      )}
    </span>
  );
}

/**
 * Account-wide "Overview" tab for `/hexa` (REV20 spec): a roster totals strip plus one progress
 * row per character — avatar/name/class/level, the overall % as a `RingProgress` around the
 * hexagon glyph (matching the shared roster cards and `HexaProgressWidget`'s goal ring),
 * used/remaining frags + Sol Erda, and the same per-node ring strip the per-character tracker's
 * goal widget carries (`HexaNodeRingStrip` — one ringed skill icon per node, replacing the old
 * `hexaTypeChips` text chips). Erda Link classes (`sixthJobSystem === 'ERDA_LINK'` — currently
 * none seeded; see `isErdaLinkClass`) don't use the HEXA Matrix at all: they get a "not tracked"
 * badge instead of a bar/chips and are excluded from the totals strip.
 *
 * REV22 Fix 2b (docs/specs/REV22-hexa-stat-and-merged-masteries.md): both the totals strip and
 * every per-character row's rollup now fold in each character's HEXA Stat matrix (`statNodes`) —
 * `EstimatePrefix` marks a figure with "≈" once any contributing rollup is `estimated`.
 */
export function HexaOverview({ characters, classes }: HexaOverviewProps) {
  if (characters.length === 0) {
    return <p className="text-sm text-text-muted">Add a character under Characters to start tracking HEXA.</p>;
  }

  const trackedCharacters = characters.filter((c) => !isErdaLinkClass(c.classSlug, classes));
  const totals = trackedCharacters.reduce(
    (acc, c) => {
      const r = hexaRollup(c.nodes, c.statNodes);
      return {
        usedFragments: acc.usedFragments + r.usedFragments,
        totalFragments: acc.totalFragments + r.totalFragments,
        usedSolErda: acc.usedSolErda + r.usedSolErda,
        totalSolErda: acc.totalSolErda + r.totalSolErda,
        // REV22 Fix 2b: the roster totals read as estimates the moment any one tracked character's
        // own rollup does (their HEXA Stat cost folded in, per hexaRollup's own doc comment).
        estimated: acc.estimated || r.estimated,
      };
    },
    { usedFragments: 0, totalFragments: 0, usedSolErda: 0, totalSolErda: 0, estimated: false },
  );
  const overallPct = totals.totalFragments > 0 ? Math.round((totals.usedFragments / totals.totalFragments) * 100) : 0;

  return (
    <div className="space-y-4">
      {trackedCharacters.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
          <div>
            <p className="font-display text-xs font-semibold uppercase tracking-wider text-text-faint">
              Roster fragments
            </p>
            <p className="mt-0.5 text-lg font-semibold text-text">
              <EstimatePrefix estimated={totals.estimated} />
              {totals.usedFragments.toLocaleString()}/{totals.totalFragments.toLocaleString()}{' '}
              <HexaCurrencyIcon currency="sol-erda-fragment" />
            </p>
          </div>
          <div>
            <p className="font-display text-xs font-semibold uppercase tracking-wider text-text-faint">
              Roster Sol Erda
            </p>
            <p className="mt-0.5 text-lg font-semibold text-text">
              <EstimatePrefix estimated={totals.estimated} />
              {totals.usedSolErda.toLocaleString()}/{totals.totalSolErda.toLocaleString()}{' '}
              <HexaCurrencyIcon currency="sol-erda" />
            </p>
          </div>
          <div className="ml-auto">
            <p className="font-display text-xs font-semibold uppercase tracking-wider text-text-faint">Overall</p>
            <p className="mt-0.5 text-lg font-semibold text-text">{overallPct}%</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {characters.map((character) => {
          if (isErdaLinkClass(character.classSlug, classes)) {
            return (
              <div
                key={character.characterId}
                className="card-hover flex flex-wrap items-center gap-3 rounded-xl border border-border bg-bg-elevated p-4"
              >
                <RowAvatar src={character.avatarSrc} name={character.name} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-text">{character.name}</p>
                  <p className="truncate text-xs text-text-faint">
                    {classDisplayName(character.classSlug, classes)} · Lv.{character.level}
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-bg-elevated-hover px-3 py-1 text-xs font-semibold uppercase tracking-wider text-text-faint">
                  Erda Link — not tracked
                </span>
              </div>
            );
          }

          const rollup = hexaRollup(character.nodes, character.statNodes);
          // Same "all nodes at goal" success flip as HexaProgressWidget's ring and the shared
          // roster cards — overallPct alone can round to 100 before every node is actually there.
          const allDone = character.nodes.length > 0 && character.nodes.every((n) => n.maxed);

          return (
            <div key={character.characterId} className="card-hover space-y-3 rounded-xl border border-border bg-bg-elevated p-4">
              <div className="flex items-center gap-3">
                <RowAvatar src={character.avatarSrc} name={character.name} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-text">{character.name}</p>
                  <p className="truncate text-xs text-text-faint">
                    {classDisplayName(character.classSlug, classes)} · Lv.{character.level}
                  </p>
                </div>
                <p className="shrink-0 text-sm font-semibold text-text">{rollup.overallPct}%</p>
                <RingProgress
                  value={rollup.overallPct}
                  size={48}
                  thickness={3.5}
                  color={allDone ? 'var(--success)' : 'var(--accent)'}
                >
                  <IconHexa className="size-5 text-text-muted" />
                </RingProgress>
              </div>

              <p className="text-xs text-text-muted">
                <EstimatePrefix estimated={rollup.estimated} />
                {rollup.usedFragments.toLocaleString()}/{rollup.totalFragments.toLocaleString()}{' '}
                <HexaCurrencyIcon currency="sol-erda-fragment" /> · {rollup.usedSolErda.toLocaleString()}/
                {rollup.totalSolErda.toLocaleString()} <HexaCurrencyIcon currency="sol-erda" />
              </p>

              {character.nodes.length > 0 && <HexaNodeRingStrip nodes={character.nodes} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
